import type React from 'react'
import { forwardRef, useEffect } from 'react'
import { Box, Flex, Text } from '@chakra-ui/react'
import { useFormColors } from '../forms/FormThemeContext'
import { useResolveText } from '../i18n'

export interface WithFormLabel {
  required?: boolean
  label?: string
  labelTid?: string
  labelValues?: Record<string, unknown>
  name: string
  error?: string
  helper?: React.ReactNode
  helperTid?: string
  helperValues?: Record<string, unknown>
  /** Maximum allowed characters. Renders a counter below the field and truncates
   *  values that arrive longer than the limit (e.g. from setValue/fetch). The
   *  native `maxLength` attribute is also forwarded to the input so the browser
   *  prevents typing past the limit. */
  maxLength?: number
}

export const withFormLabel = <P extends object>(WrappedComponent: React.ComponentType<P>) => {
  const ComponentWithFormLabel = forwardRef<unknown, P & WithFormLabel>(({ error, helper, helperTid, helperValues, label, labelTid, labelValues, required, maxLength, ...props }, ref) => {
    const c = useFormColors()
    const resolve = useResolveText()
    const labelText = resolve(labelTid, label, labelValues)
    const helperText = resolve(helperTid, typeof helper === 'string' ? helper : undefined, helperValues)
    const helperNode = helperText ?? (typeof helper !== 'string' ? helper : undefined)
    // Error messages from zod schemas may arrive as i18n keys
    // ("personas.nuevo.zod.required") or plain Spanish fallbacks. Run them
    // through resolve(): when the string is a registered translation key we
    // get the localized message; otherwise i18next returns the key unchanged
    // so plain text keeps rendering as-is.
    const errorText = error ? resolve(error, error) : undefined

    const rawValue = (props as Record<string, unknown>).value
    const valueStr = typeof rawValue === 'string' ? rawValue : rawValue == null ? '' : String(rawValue)
    const onChange = (props as { onChange?: (e: unknown) => void }).onChange

    // Truncate values that arrive longer than maxLength (programmatic setValue
    // from a fetch response, etc.) so the form value stays within bounds.
    useEffect(() => {
      if (typeof maxLength !== 'number') return
      if (valueStr.length <= maxLength) return
      if (typeof onChange !== 'function') return
      onChange(valueStr.slice(0, maxLength))
    }, [maxLength, valueStr, onChange])

    const showCounter = typeof maxLength === 'number'
    const counterCount = Math.min(valueStr.length, maxLength ?? 0)

    const forwardedProps =
      typeof maxLength === 'number' ? { ...(props as P), maxLength } : (props as P)

    return (
      <Box mb={2}>
        {labelText && (
          <Text fontSize="sm" fontWeight="500" color={c.textSecondary} mb={2}>
            {labelText}
            {required && <Text as="span" color={c.error}> *</Text>}
          </Text>
        )}
        <WrappedComponent ref={ref} {...forwardedProps} />
        {(helperNode || errorText || showCounter) && (
          <Flex justifyContent="space-between" alignItems="flex-start" mt={1} gap={2}>
            <Box flex="1" minW={0}>
              {helperNode && !error && (
                <Text fontSize="xs" color={c.textDimmed}>{helperNode}</Text>
              )}
              {errorText && (
                <Text fontSize="xs" color={c.error}>{errorText}</Text>
              )}
            </Box>
            {showCounter && (
              <Text
                fontSize="xs"
                color={counterCount >= (maxLength as number) ? c.error : c.textDimmed}
                flexShrink={0}
                fontVariantNumeric="tabular-nums"
              >
                {counterCount}/{maxLength}
              </Text>
            )}
          </Flex>
        )}
      </Box>
    )
  })
  ComponentWithFormLabel.displayName = `WithFormLabel-${WrappedComponent.displayName || 'WrappedFormElement'}`
  return ComponentWithFormLabel
}
